import React, { useState, useEffect } from 'react';
import { IconButton, Modal } from '@greystar/designsystem';
import styles from './imageZoonInOut.module.scss';
import { useFeesAnalytics, useAnalytics, getDictionaryEntry } from '@greystar/lib';
import { useI18n } from 'next-localization';

function ImageZoomInOut({ imageUrl }: { readonly imageUrl: string }) {
  const [scale, setScale] = useState(1);
  const [isDragging, setIsDragging] = useState(false);
  const [prevPosition, setPrevPosition] = useState({ x: 0, y: 0 });
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isFullScreen, setIsFullScreen] = useState(false);

  const { mergeAnalyticsData } = useFeesAnalytics();
  const analytics = useAnalytics();
  const { t } = useI18n();

  const updateZoomData = () => {
    mergeAnalyticsData({
      overrides: {
        event: 'unit_rent_calc_floor_plan_zoom',
        action_type: 'click',
        zoom: scale,
        click_text: 'Zoom',
      },
      onComplete: (data) => {
        analytics.trackEvent('unit_rent_calc_floor_plan_zoom', data);
      },
    });
  };

  const updateFullscreenData = () => {
    mergeAnalyticsData({
      overrides: {
        event: 'unit_rent_calc_floor_plan_full_screen',
        action_type: 'click',
        zoom: scale,
        click_text: 'Full Screen',
      },
      onComplete: (data) => {
        analytics.trackEvent('unit_rent_calc_floor_plan_full_screen', data);
      },
    });
  };

  const handleZoomIn = () => {
    if (scale >= 2) return;
    setScale((prev) => prev + 0.2);
    updateZoomData();
  };

  const handleZoomOut = () => {
    if (scale <= 0.4) return;
    setScale((prev) => prev - 0.2);
    updateZoomData();
  };

  const handleFullScreen = () => {
    setIsFullScreen(!isFullScreen);
    updateFullscreenData();
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (isFullScreen) return;
    setIsDragging(true);
    setPrevPosition({ x: e.clientX, y: e.clientY });
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isDragging) return;
    const deltaX = e.clientX - prevPosition.x;
    const deltaY = e.clientY - prevPosition.y;
    setPrevPosition({ x: e.clientX, y: e.clientY });
    setPosition((prev) => ({
      x: prev.x + deltaX,
      y: prev.y + deltaY,
    }));
  };

  const handleMouseUp = () => {
    const hasPositionChanged = position.x !== prevPosition.x || position.y !== prevPosition.y;

    if (isDragging && hasPositionChanged) {
      mergeAnalyticsData({
        overrides: {
          event: 'unit_rent_calc_floor_plan_pan',
          action_type: 'click',
          click_text: 'Pan',
        },
        onComplete: (data) => {
          analytics.trackEvent('unit_rent_calc_floor_plan_pan', data);
        },
      });
    }
    setIsDragging(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (scale > 1) {
      const moveAmount = 10;
      switch (e.key) {
        case 'ArrowUp':
          setPosition((prev) => ({ ...prev, y: prev.y + moveAmount }));
          e.preventDefault();
          break;
        case 'ArrowDown':
          setPosition((prev) => ({ ...prev, y: prev.y - moveAmount }));
          e.preventDefault();
          break;
        case 'ArrowLeft':
          setPosition((prev) => ({ ...prev, x: prev.x + moveAmount }));
          e.preventDefault();
          break;
        case 'ArrowRight':
          setPosition((prev) => ({ ...prev, x: prev.x - moveAmount }));
          e.preventDefault();
          break;
      }
    }
  };

  useEffect(() => {
    const handleGlobalMouseUp = () => setIsDragging(false);

    window.addEventListener('mouseup', handleGlobalMouseUp);

    return () => {
      window.removeEventListener('mouseup', handleGlobalMouseUp);
    };
  }, []);

  return (
    <div className={styles['image-zoom']}>
      <div className={styles['image-zoom-wrapper']}>
        <div className={styles['image-zoom-wrapper-btn-container']}>
          <IconButton
            iconName="Plus"
            variant={'floatingPrimary'}
            onClick={handleZoomIn}
            isDisabled={false}
            aria-label={getDictionaryEntry(t('floor-plan-zoom-in'), 'Zoom In Floor Plan')}
          />
          <IconButton
            iconName="Minus"
            variant={'floatingPrimary'}
            onClick={handleZoomOut}
            isDisabled={false}
            aria-label={getDictionaryEntry(t('floor-plan-zoom-out'), 'Zoom Out Floor Plan')}
          />
          <IconButton
            iconName="Enlarge"
            variant={'floatingPrimary'}
            onClick={handleFullScreen}
            isDisabled={false}
            aria-label={getDictionaryEntry(t('floor-plan-full-screen'), 'Full Screen Floor Plan')}
          />
        </div>
        <div className={styles['image-zoom-wrapper-inner']}>
          <div
            id="imageZoomContainer"
            className={styles['image-zoom-wrapper-inner-img-container']}
            style={{
              transform: `scale(${scale}) translate(${position.x}px, ${position.y}px)`,
              transformOrigin: 'center center',
            }}
            role="application"
            aria-label="Zoomable and draggable floor plan image"
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onKeyDown={handleKeyDown}
          >
            <img
              src={imageUrl}
              alt={'Floor plan'}
              className={styles['image-zoom-wrapper-inner-img-container-floor-img']}
              style={{ imageRendering: 'pixelated' }}
              draggable={false}
            />
          </div>
        </div>
      </div>

      <p className={styles['image-zoom-disclaimer']}>
        Floor plans are artist&apos;s rendering. Dimensions are approximate. Actual product and
        specifications may vary in dimension or detail. Not all features are available in every
        rental home.
      </p>
      {isFullScreen && (
        <Modal>
          <Modal.Header onClose={() => setIsFullScreen(false)} />
          <Modal.Body>
            <img
              src={imageUrl}
              alt={'Floor plan'}
              className={styles['image-zoom-wrapper-inner-img-container-floor-img']}
              style={{ imageRendering: 'pixelated' }}
              draggable={false}
            />
          </Modal.Body>
        </Modal>
      )}
    </div>
  );
}

export default ImageZoomInOut;
